import { z } from "zod";
import { PairSimProfileSchema } from "./compatibility.js";
import { SimProfileSchema } from "./scenarios.js";
import { ConfidenceBasisSchema, TrackRecordSourceSchema } from "./track-record.js";

// ── Preview leve síncrono (efêmero, imediato) ─────────────────────────────────
// Read-model transitório: NÃO cria Simulation, NÃO entra na fila. Espelha o motor leve TS
// do cliente para consistência com dados de servidor (janela de calibração). Ver
// scenario-preview.service.ts (`POST /scenarios/:id/preview`).

// Zod da premissa por parâmetro (espelha a interface SimulationDataSource acima).
export const SimulationDataSourceSchema = z.object({
  component: z.enum(["availability", "performance", "quality"]),
  source: z.enum(["sensor", "integration", "import", "calculated", "manual"]),
  machineId: z.string().uuid().optional(),
  parameterName: z.string().optional(),
  origin: z.enum(["measured", "assumed"]).optional(),
  defaulted: z.boolean().optional(),
});

// ── QUAL expediente esta simulação está usando ────────────────────────────────────────
// Quatro valores porque são quatro respostas diferentes para "de onde saiu a jornada que o motor
// simulou", e três delas viram o mesmo número de horas em muitos períodos. Sem o rótulo, quem abre
// o cenário só consegue distinguir "há calendário" de "não há" (comparando horas corridas com
// operacionais) — e é exatamente a distinção que NÃO importa quando o resultado surpreende.
//   organization — herdou o expediente da fábrica (a linha base não declarou o dela)
//   line         — herdou o expediente próprio da linha base
//   scenario     — cópia editada dentro do cenário, que não corresponde a nenhuma linha real
//   continuous   — 24 h por dia (`always_on`, ou nenhuma camada declarou expediente)
export const ScenarioCalendarBasisSchema = z.enum([
  "organization",
  "line",
  "scenario",
  "continuous",
]);
export type ScenarioCalendarBasis = z.infer<typeof ScenarioCalendarBasisSchema>;

export const PreviewResponseSchema = z.object({
  runLevel: z.literal("preview"),
  availability: z.number().finite().nullable(),
  performance: z.number().finite().nullable(),
  quality: z.number().finite().nullable(),
  oee: z.number().finite().nullable(),
  estimatedProduction: z.number().finite().nullable(),
  // Horas OPERACIONAIS do período (∩ calendário efetivo). Igual às horas corridas
  // quando 24/7. O cliente exibe "Nh corridas · Mh operacionais" e NUNCA recalcula a regra.
  effectiveHours: z.number().finite().nonnegative(),
  // Horas CORRIDAS da janela REAL (FREE = período; OP = janela da OP,
  // que o cliente não conhece). "Tem calendário" ⇔ effectiveHours < runningHours (24/7 ⇒ iguais).
  runningHours: z.number().finite().nonnegative(),
  // A base de expediente da rodada, para a tela dizê-la sem que o usuário abra
  // a configuração do cenário. Obrigatório de propósito: o default silencioso seria "contínuo", que
  // é a resposta errada mais cara de todas — a que faz um cenário de 8 h/dia passar por 24/7.
  calendarBasis: ScenarioCalendarBasisSchema,
  bottleneckStageKey: z.string().nullable(),
  perStage: z.array(z.object({ stageKey: z.string(), throughputUnH: z.number() })),
  confidenceLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]),
  // ── Spec 079 (R1) — a resolução AO VIVO, sem persistir nada ────────────────────────────────
  // O preview leve continua efêmero (075: não cria `Simulation`, não enfileira, não audita) — a
  // régua é lida, aplicada e descartada. Os três campos existem aqui pela MESMA razão que
  // existem na execução: o gestor decide olhando o preview, e um preview que anuncia `HIGH` por
  // procedência enquanto a rodada anuncia `LOW` por histórico é a divergência que a SC-003 proíbe.
  // ⚠️ `.nullable().optional()` e não obrigatórios: linha sem régua não tem o que declarar, e
  // exigir os campos forçaria um valor inventado para "não havia régua".
  displayedConfidenceLevel: z.enum(["HIGH", "MEDIUM", "LOW", "INSUFFICIENT"]).nullable().optional(),
  confidenceBasis: ConfidenceBasisSchema.nullable().optional(),
  trackRecordSource: TrackRecordSourceSchema.nullable().optional(),
  dataSources: z.array(SimulationDataSourceSchema),
  // Base MEDIDA por máquina (pré-delta) re-derivada na janela; o sheet do sandbox a usa
  // como base de exibição do slider quando não há perfil autorado. `.default([])` p/ dados legados.
  // Contador + volume na janela: a UI decide o aviso
  // (estrutural = sem contador, com CTA; transitório = volume < piso, sem CTA).
  machines: z
    .array(
      z.object({
        key: z.string(),
        baseProfile: SimProfileSchema,
        productionCounterConfigured: z.boolean().default(false),
        producedInWindow: z.number().nonnegative().default(0),
        // Perfil autorado do par com o produto ATIVO do cenário. O servidor
        // ENTREGA o dado; o cliente aplica com o MESMO helper (applyPairProfile), sem
        // reimplementar a regra. Vem por máquina porque o preview local precisa dele mesmo
        // quando há perfil autorado por máquina (o par é mais específico e vence).
        pairProfile: PairSimProfileSchema.nullable().default(null),
      })
    )
    .default([]),
});
export type PreviewResponse = z.infer<typeof PreviewResponseSchema>;
